import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { HttpClient, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { APP_ID, APP_INITIALIZER, CSP_NONCE, ErrorHandler, inject, Injector, NgModule, Optional, PLATFORM_ID, TransferState } from '@angular/core';
import { BrowserModule, provideClientHydration, withEventReplay, withNoHttpTransferCache } from '@angular/platform-browser';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { NgxPageScrollModule } from 'ngx-page-scroll';
import { NgxPageScrollCoreModule } from 'ngx-page-scroll-core';
import { GlobalModule } from '@techmore/global';
import { MAT_LUXON_DATE_ADAPTER_OPTIONS } from '@angular/material-luxon-adapter';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { UrlSerializer } from '@angular/router';
import { UPLOAD_ASSETS, UploadModule } from '@techmore/upload';
import { WOW_ASSETS } from '@techmore/wow';
import { Response } from 'express';
import { RESPONSE } from '../express.tokens';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HtmlSuffixURLSerializer } from './share/entity/html-suffix-url-serializer';
import { GlobalErrorHandlerService } from './share/service/global-error-handler.service';
import { translateBrowserLoaderFactory } from './share/service/translate-browser-loader.service';
import { CSP_NONCE_KEY } from './share/tokens/csp-nonce.statekey';
import { GENERATOR_ENV_TOKEN, GeneratorEnv } from './share/tokens/generator-env.token';
import { IS_BROWSER_TOKEN } from './share/tokens/is-browser.token';
import { REQUEST_MARK_KEY } from './share/tokens/request-mark.statekey';
import { REQUEST_MARK_TOKEN } from './share/tokens/request-mark.token';
import { environment } from '../environments/environment';
import { initializationFactory } from './initializationFactory.fn';
import { CustomLuxonDateAdapter } from './share/entity/custom-luxon-date-adapter';
import { PhotoSwipeModule } from '@techmore/photo-swipe';


export const DATE_FORMATS = {
  parse: {
    dateInput: 'yyyy/MM/dd',
  },
  display: {
    dateInput: 'yyyy/MM/dd',
    monthYearLabel: 'yyyy MMMM',
    dateA11yLabel: 'DDD',
    monthYearA11yLabel: 'yyyy MMMM',
  },
};

@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: translateBrowserLoaderFactory,
        deps: [HttpClient, TransferState, DOCUMENT]
      },
      defaultLanguage: 'zh-tw',
    }),
    NgxPageScrollCoreModule,
    NgxPageScrollModule,
    GlobalModule.forRoot(),
    UploadModule.forRoot(),
    PhotoSwipeModule.forRoot(),
  ],
  providers: [
    { provide: APP_ID, useValue: 'serverApp' },
    {
      provide: GENERATOR_ENV_TOKEN, useFactory: () => {
        return Object.assign(new GeneratorEnv(), {
          apiHost: inject(IS_BROWSER_TOKEN) ? environment.ipApiHost : environment.apiHost,
          editApiHost: environment.editApiHost,
          allowBackStageDomain: environment.allowBackStageDomain,
          recaptchaKey: environment.recaptchaKey,
          commonToken: environment.commonToken,
          accessTokenKey: environment.accessTokenKey,
          refreshTokenKey: environment.refreshTokenKey,
          mode: environment.mode,
        }) as GeneratorEnv;
      }, multi: false
    },
    { provide: APP_INITIALIZER, useFactory: initializationFactory, deps: [Injector], multi: true },
    { provide: REQUEST_MARK_TOKEN, deps: [PLATFORM_ID, TransferState, [new Optional(), RESPONSE]], useFactory: (platformId: object, tState: TransferState, response: Response) => {
      if (isPlatformBrowser(platformId)) {
        return tState.get(REQUEST_MARK_KEY, '');
      } else {
        return response.locals['requestMark'];
      }
    }},
    {
      provide: CSP_NONCE, deps: [TransferState, [new Optional(), RESPONSE]], useFactory: (tState: TransferState, response: Response) => {
        if (inject(IS_BROWSER_TOKEN)) {
          return tState.get(CSP_NONCE_KEY, '');
        }
        return response.locals['cspNonce'];
      }
    },
    {provide: WOW_ASSETS, useFactory: () => {
      return {scripts: [environment.baseHref + 'assets/wow/wow.min.js'], styles:  [environment.baseHref + 'assets/animate/v3/animate.css'] }
    }},
    {provide: UPLOAD_ASSETS, useFactory: () => {
      return {scripts: [], styles:  [environment.baseHref + 'uploaderStyle.bundle.css'] }
    }},
    { provide: MAT_DATE_LOCALE, useValue: 'zh-TW' },
    {
      provide: MAT_LUXON_DATE_ADAPTER_OPTIONS,
      useValue: {
        useUtc: false,
        defaultOutputCalendar: 'gregory',
      }
    },
    {
      provide: DateAdapter,
      useClass: CustomLuxonDateAdapter,
      deps: [MAT_DATE_LOCALE, MAT_LUXON_DATE_ADAPTER_OPTIONS],
    },

    { provide: MAT_DATE_FORMATS, useValue: DATE_FORMATS },
    { provide: UrlSerializer, useClass: HtmlSuffixURLSerializer },
    { provide: ErrorHandler, useClass: GlobalErrorHandlerService },
    provideHttpClient(withInterceptorsFromDi(), withFetch()),
    provideClientHydration(
      withEventReplay(),
      withNoHttpTransferCache(),
      // withHttpTransferCacheOptions({
      //   includePostRequests: true,
      // }),
    ),
    provideAnimationsAsync(),
  ],
  bootstrap: [AppComponent]
})
export class AppModule { }
